import React, { useCallback, useEffect, useState } from "react";
import "./AboutPage.css";
import promiseImg from "../components/Assets/Promise2.jpg";
import { API_BASE } from "../config/api";
import { DEFAULT_ABOUT_PAGE, normalizeAboutSections } from "../utils/aboutPage";
import { sanitizeRichTextHtml } from "../utils/richText";
import { bindStorefrontRefresh } from "../utils/storefrontRefresh";

const getAlignmentClass = (textAlign) =>
  textAlign === "center" ? "center" : "left";

const getCardSectionThemeClass = (theme) =>
  theme === "light"
    ? "about-card-section about-card-section--light"
    : "about-card-section about-card-section--gradient";

const getGridSectionThemeClass = (theme) =>
  theme === "light"
    ? "about-why-section about-why-section--light"
    : "about-why-section about-why-section--gradient";

const getSplitSectionThemeClass = (theme) =>
  theme === "light"
    ? "about-promise about-promise--light"
    : "about-promise about-promise--gradient";

const renderRichText = (html, className) => {
  const sanitizedHtml = sanitizeRichTextHtml(html);

  if (!sanitizedHtml) {
    return null;
  }

  return (
    <div
      className={`${className} about-rich-text`}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
};

const renderInlineHtml = (html, className) => {
  const sanitizedHtml = sanitizeRichTextHtml(html);

  if (!sanitizedHtml) {
    return null;
  }

  return (
    <span
      className={className}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
};

const getVisibleItems = (section) =>
  Array.isArray(section?.items)
    ? section.items.filter(
        (item) => item?.visible !== false && sanitizeRichTextHtml(item?.html)
      )
    : [];

const renderSection = (section) => {
  const visibleItems = getVisibleItems(section);

  if (!section?.visible || (!section.title && !visibleItems.length && !section.imageUrl)) {
    return null;
  }

  if (section.type === "intro") {
    return (
      <section className="about-intro" key={section.id}>
        <div className="about-intro-shell">
          <div className="about-intro-card">
            {section.title && (
              <h2 className="about-section-title center about-brand-name">
                {section.title}
              </h2>
            )}
            <div className="about-intro-line" />
            {visibleItems.map((item) => (
              <React.Fragment key={item.id}>
                {renderRichText(item.html, "about-intro-text")}
              </React.Fragment>
            ))}
            <div className="about-intro-line" />
          </div>
        </div>
      </section>
    );
  }

  if (section.type === "split") {
    const imageSource =
      section.imageUrl || (section.id === "our-promise" ? promiseImg : "");

    return (
      <section className={getSplitSectionThemeClass(section.theme)} key={section.id}>
        <div className="about-promise-text" style={{ textAlign: section.textAlign }}>
          {section.title && (
            <h2
              className={`about-section-title ${getAlignmentClass(section.textAlign)}`}
            >
              {section.title}
            </h2>
          )}
          {visibleItems.map((item) => (
            <React.Fragment key={item.id}>
              {renderRichText(item.html, "about-split-text")}
            </React.Fragment>
          ))}
        </div>
        {imageSource && (
          <div className="about-promise-deco">
            <img
              src={imageSource}
              alt={section.imageAlt || section.title || "About Us"}
              className="about-promise-diamond-img"
            />
          </div>
        )}
      </section>
    );
  }

  if (section.type === "grid") {
    return (
      <section className={getGridSectionThemeClass(section.theme)} key={section.id}>
        {section.title && (
          <h2
            className={`about-section-title ${getAlignmentClass(section.textAlign)}`}
          >
            {section.title}
          </h2>
        )}
        <div className="about-why-grid">
          {visibleItems.map((item) => (
            <div key={item.id} className="about-why-card">
              {section.icon ? renderInlineHtml(section.icon, "about-why-icon") : null}
              {renderRichText(item.html, "about-grid-copy")}
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className={getCardSectionThemeClass(section.theme)} key={section.id}>
      <div className="about-card">
        {section.icon ? renderInlineHtml(section.icon, "about-card-icon") : null}
        <div style={{ textAlign: section.textAlign }}>
          {section.title && (
            <h2
              className={`about-section-title ${getAlignmentClass(section.textAlign)}`}
            >
              {section.title}
            </h2>
          )}
          {visibleItems.map((item) => (
            <React.Fragment key={item.id}>
              {renderRichText(item.html, "about-card-copy")}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

const AboutPage = () => {
  const [sections, setSections] = useState(DEFAULT_ABOUT_PAGE.sections);

  const fetchAboutPage = useCallback(() => {
    let isMounted = true;

    fetch(`${API_BASE}/about-page`)
      .then((response) => response.json())
      .then((data) => {
        if (!isMounted) {
          return;
        }

        setSections(normalizeAboutSections(data));
      })
      .catch(() => {
        if (isMounted) {
          setSections(DEFAULT_ABOUT_PAGE.sections);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const cleanup = fetchAboutPage();
    return cleanup;
  }, [fetchAboutPage]);

  useEffect(() => bindStorefrontRefresh(() => fetchAboutPage(), { scopes: ['about'] }), [fetchAboutPage]);

  return (
    <div className="about-page">
      <div className="about-body">
        {sections.map((section) => renderSection(section))}
      </div>
    </div>
  );
};

export default AboutPage;
